import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TokenTypeBadge } from "./TokenTypeBadge";
import { ExpirationBadge } from "./ExpirationBadge";
import { EmptyTokenState } from "./EmptyTokenState";
import type { AccessToken } from "@/types/token";

interface TokenListProps {
  tokens: AccessToken[];
  isLoading?: boolean;
  onRevoke?: (tokenId: string) => void;
  onCreateToken?: () => void;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-5 w-[200px]" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-[64px] rounded-full" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <Skeleton className="h-4 w-[80px]" />
                <Skeleton className="h-4 w-[140px]" />
              </div>
              <div className="flex items-center gap-4">
                <Skeleton className="h-5 w-[100px] rounded-full" />
                <Skeleton className="h-8 w-[68px]" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TokenCard({
  token,
  onRevoke,
}: {
  token: AccessToken;
  onRevoke?: (tokenId: string) => void;
}) {
  const isRevoked = !!token.revoked_at;

  return (
    <Card
      className={isRevoked ? "opacity-60" : undefined}
      data-testid="token-card"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base font-medium" data-testid="token-name">
            {token.name}
          </CardTitle>
          <div className="flex items-center gap-2">
            <TokenTypeBadge type={token.token_type} />
            {isRevoked && <Badge variant="destructive">revoked</Badge>}
          </div>
        </div>
        {token.description && (
          <p className="text-sm text-muted-foreground">{token.description}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <code className="font-mono text-muted-foreground">
              {token.token_prefix}
            </code>
            <time
              dateTime={token.created_at}
              className="font-mono text-muted-foreground"
            >
              Created {formatDate(token.created_at)}
            </time>
          </div>
          <div className="flex items-center gap-4">
            <ExpirationBadge expiresAt={token.expires_at} />
            {onRevoke && (
              <Button
                variant="outline"
                size="sm"
                disabled={isRevoked}
                onClick={() => onRevoke(token.id)}
              >
                Revoke
              </Button>
            )}
          </div>
        </div>
        {token.allowed_origins && token.allowed_origins.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-muted-foreground mb-1">
              Allowed Origins:
            </p>
            <div className="flex flex-wrap gap-1">
              {token.allowed_origins.map((origin) => (
                <Badge
                  key={origin}
                  variant="outline"
                  className="font-mono text-xs"
                >
                  {origin}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function TokenList({
  tokens,
  isLoading,
  onRevoke,
  onCreateToken,
}: TokenListProps) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!tokens || tokens.length === 0) {
    return <EmptyTokenState onCreateToken={onCreateToken} />;
  }

  return (
    <div className="space-y-4">
      {tokens.map((token) => (
        <TokenCard key={token.id} token={token} onRevoke={onRevoke} />
      ))}
    </div>
  );
}
